import React, { useRef, useEffect } from "react";
import { OptimizedBrick } from "../../utils/brickOptimizer";
import { getColorHex } from "../../constants/legoColors";

interface IsometricBrickRendererProps {
  bricks: OptimizedBrick[];
  highlightBrick?: OptimizedBrick;
  width?: number;
  height?: number;
  scale?: number;
}

const IsometricBrickRenderer: React.FC<IsometricBrickRendererProps> = ({
  bricks,
  highlightBrick,
  width = 400,
  height = 300,
  scale = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Set background (warm surface, per .impeccable.md)
    ctx.fillStyle = "#f8f5f0";
    ctx.fillRect(0, 0, width, height);

    const brickHeight = 16 * scale;
    const brickDepth = 12 * scale;
    const studRadius = 3.5 * scale;

    // Calculate center offset
    const centerX = width / 2;
    const centerY = height / 2;

    // Sort bricks for proper rendering (back to front)
    const sortedBricks = [...bricks].sort((a, b) => {
      const aIso = a.y - a.x;
      const bIso = b.y - b.x;
      if (aIso !== bIso) return aIso - bIso;
      return a.y - b.y;
    });

    // Render each brick
    sortedBricks.forEach((brick) => {
      const isHighlighted = highlightBrick && 
        brick.x === highlightBrick.x && 
        brick.y === highlightBrick.y &&
        brick.colorIndex === highlightBrick.colorIndex;

      drawIsometricBrick(
        ctx,
        brick,
        centerX,
        centerY,
        brickHeight,
        brickDepth,
        studRadius,
        isHighlighted || false
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bricks, highlightBrick, width, height, scale]);

  const drawIsometricBrick = (
    ctx: CanvasRenderingContext2D,
    brick: OptimizedBrick,
    centerX: number,
    centerY: number,
    brickHeight: number,
    brickDepth: number,
    studRadius: number,
    isHighlighted: boolean
  ) => {
    const brickW = brick.brickType.width * 20 * scale;
    const brickH = brick.brickType.height * 20 * scale;

    // Isometric projection
    const isoX = (brick.x - brick.y) * 20 * scale * 0.866;
    const isoY = (brick.x + brick.y) * 20 * scale * 0.5;

    const x = centerX + isoX;
    const y = centerY + isoY - brickDepth;

    const color = getColorHex(brick.colorIndex);

    // Draw brick faces
    ctx.save();

    // Top face — grid x-axis projects to (0.866, 0.5), grid y-axis to (-0.866, 0.5)
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + brickW * 0.866, y + brickW * 0.5);
    ctx.lineTo(x + brickW * 0.866 - brickH * 0.866, y + brickW * 0.5 + brickH * 0.5);
    ctx.lineTo(x - brickH * 0.866, y + brickH * 0.5);
    ctx.closePath();
    ctx.fillStyle = adjustBrightness(color, 1.0);
    ctx.fill();
    ctx.strokeStyle = isHighlighted ? "#FFD700" : "rgba(0,0,0,0.3)";
    ctx.lineWidth = isHighlighted ? 3 : 1;
    ctx.stroke();

    // Left face (spans the grid y-axis from the anchor)
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - brickH * 0.866, y + brickH * 0.5);
    ctx.lineTo(x - brickH * 0.866, y + brickH * 0.5 + brickDepth);
    ctx.lineTo(x, y + brickDepth);
    ctx.closePath();
    ctx.fillStyle = adjustBrightness(color, 0.7);
    ctx.fill();
    ctx.strokeStyle = isHighlighted ? "#FFD700" : "rgba(0,0,0,0.3)";
    ctx.lineWidth = isHighlighted ? 3 : 1;
    ctx.stroke();

    // Right face (spans the grid y-axis from the far x-edge)
    ctx.beginPath();
    ctx.moveTo(x + brickW * 0.866, y + brickW * 0.5);
    ctx.lineTo(x + brickW * 0.866 - brickH * 0.866, y + brickW * 0.5 + brickH * 0.5);
    ctx.lineTo(x + brickW * 0.866 - brickH * 0.866, y + brickW * 0.5 + brickH * 0.5 + brickDepth);
    ctx.lineTo(x + brickW * 0.866, y + brickW * 0.5 + brickDepth);
    ctx.closePath();
    ctx.fillStyle = adjustBrightness(color, 0.85);
    ctx.fill();
    ctx.strokeStyle = isHighlighted ? "#FFD700" : "rgba(0,0,0,0.3)";
    ctx.lineWidth = isHighlighted ? 3 : 1;
    ctx.stroke();

    // Draw studs on top
    const studsX = brick.brickType.width;
    const studsY = brick.brickType.height;

    for (let sy = 0; sy < studsY; sy++) {
      for (let sx = 0; sx < studsX; sx++) {
        const studOffsetX = sx * 20 * scale;
        const studOffsetY = sy * 20 * scale;

        const studIsoX = (studOffsetX - studOffsetY) * 0.866;
        const studIsoY = (studOffsetX + studOffsetY) * 0.5;

        const studX = x + studIsoX + 10 * scale * 0.866;
        const studY = y + studIsoY + 10 * scale * 0.5;

        // Draw stud
        ctx.beginPath();
        ctx.arc(studX, studY, studRadius, 0, Math.PI * 2);
        ctx.fillStyle = adjustBrightness(color, 0.9);
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.4)";
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    }

    ctx.restore();
  };

  const adjustBrightness = (color: string, factor: number): string => {
    const rgb = color.match(/\d+/g);
    if (!rgb) return color;

    const r = Math.round(parseInt(rgb[0]) * factor);
    const g = Math.round(parseInt(rgb[1]) * factor);
    const b = Math.round(parseInt(rgb[2]) * factor);

    return `rgb(${Math.min(255, r)}, ${Math.min(255, g)}, ${Math.min(255, b)})`;
  };

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="isometric-brick-canvas"
      aria-hidden="true"
      style={{ border: "1px solid var(--color-paper-2)", borderRadius: "4px" }}
    />
  );
};

export default IsometricBrickRenderer;
